import { industryMap } from "../../utils/industryMap";
import { industryMapping } from "../../utils/mappings";
import { itemsToExclude } from "../../utils/mappings";
import { useEffect, useState, useCallback, useMemo } from "react";
import { fetchItems } from "../../utils/fetchItemData";
import { useModalStack } from "../../utils/modalContext";
import { useNavigate, useParams } from "react-router-dom";

const SkillsBreakdown = () => {
  const [items, setItems] = useState([]);
  const [processedData, setProcessedData] = useState({});
  const { skillTab } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(skillTab || "farming");
  const [isLoading, setIsLoading] = useState(true);
  const { pushModal } = useModalStack();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" });

  //Item Modal & Navigation
  const handleOpenModal = async (item) => {
    try {
      console.log(item);
      const data = await fetchItems({ itemId: item });
      if (data?.[0]) {
        pushModal({
          id: `item-${item}`,
          type: "itemModal",
          props: {
            item: data[0],
            onClose: () => {},
            showNavigation: false,
            onNavigate: null,
            totalItems: 1,
            index: null,
          },
        });
      }
    } catch (err) {
      console.error("Failed to fetch item:", err);
    }
  };

  // Get all tab keys that should be displayed
  const availableTabs = useMemo(() => {
    return Object.entries(industryMap)
      .filter(([key, industry]) => industry.dataTypes && industry.image)
      .map(([key]) => key);
  }, []);

  // Sync activeTab with URL parameter
  useEffect(() => {
    if (skillTab && availableTabs.includes(skillTab)) {
      setActiveTab(skillTab);
    } else if (availableTabs.length > 0) {
      // Just set the first tab as active locally
      setActiveTab(availableTabs[0]);
    }
  }, [skillTab, availableTabs]);

  // Handle tab change and update URL
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    // Navigate to the skill tab within the current section
    navigate(`/wiki/skills/breakdown/${tabKey}`);
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      try {
        await fetchItems({ itemId: null, setItems });
      } finally {
        setIsLoading(false);
      }
    };
    loadData();
  }, []);

  const resolvePath = (path, obj) => path.split(".").reduce((acc, part) => (acc ? acc[part] : undefined), obj);

  const resolveFormulaTree = useCallback((mapping, data) => {
    // Base case: if mapping is a string, treat it as a path and resolve it
    if (typeof mapping === "string") {
      return resolvePath(mapping, data);
    }

    // If mapping is a number or literal value, return it directly
    if (typeof mapping !== "object" || mapping === null) {
      return mapping;
    }

    const context = {};
    // Recursively resolve all non-formula keys into the context
    for (const key in mapping) {
      if (key !== "formula") {
        context[key] = resolveFormulaTree(mapping[key], data);
      }
    }

    // Now evaluate the formula using the resolved context
    try {
      // eslint-disable-next-line
      const func = new Function(...Object.keys(context), `return ${mapping.formula}`);
      const result = func(...Object.values(context));
      return typeof result === "number" ? parseFloat(result.toFixed(2)) : result;
    } catch (e) {
      console.error(`Error evaluating formula "${mapping.formula}":`, e);
      return null;
    }
  }, []);

  const mapData = useCallback(
    (source, template, id) => {
      const result = {};

      for (const [key, mapping] of Object.entries(template)) {
        try {
          if (mapping === null) {
            result[key] = null;

            // ✅ Handle raw values like numbers, booleans, etc.
          } else if (typeof mapping === "number" || typeof mapping === "boolean") {
            result[key] = mapping;

            // ✅ Special handling for recipe ingredients
          } else if (key === "Recipe" && mapping.ingredients) {
            const ingredients = getValue(source, "recipe.ingredients") || [];
            result[key] = {
              ingredients: ingredients.map((ing) => ({
                id: ing.id,
                image: ing.image,
                name: ing.name,
                quantity: ing.quantity,
              })),
            };

            // ✅ Handle non-mapped values
          } else if (typeof mapping === "object" && mapping.value !== undefined) {
            result[key] = mapping.value;
          }
          // ✅ Handle string-based mappings
          else if (typeof mapping === "string") {
            result[key] = getValue(source, mapping);

            // ✅ Handle formulas
          } else if (typeof mapping === "object" && mapping.formula) {
            result[key] = resolveFormulaTree(mapping, source);

            // ✅ Handle nested objects
          } else {
            result[key] = {};
            for (const [subKey, subMapping] of Object.entries(mapping)) {
              if (subKey === "id") {
                result[key][subKey] = id;
              } else {
                if (typeof subMapping === "string") {
                  result[key][subKey] = getValue(source, subMapping);
                } else if (typeof subMapping === "object" && subMapping.formula) {
                  result[key][subKey] = resolveFormulaTree(subMapping, source);
                } else if (typeof subMapping === "object" && subMapping.value !== undefined) {
                  result[key][subKey] = subMapping.value;
                } else {
                  result[key][subKey] = subMapping;
                }
              }
            }
          }
        } catch (error) {
          console.error(`Error mapping field ${key}:`, error);
          result[key] = null;
        }
      }

      return result;
    },
    [resolveFormulaTree]
  );

  const processItems = useCallback(() => {
    // Convert itemsToExclude to a Set for faster lookups
    const excludedItemsSet = new Set(itemsToExclude);

    // Pre-filter items once
    const filteredItems = items.filter(
      (item) =>
        !excludedItemsSet.has(item.id) &&
        ((item.crop && Object.keys(item.crop).length > 0) ||
          (item.produce && item.produce.length > 0) ||
          (item.recipes && item.recipes.length > 0) ||
          (item.craftingMap && item.craftingMap.length > 0))
    );

    const result = {};

    // Process each industry
    for (const [industryKey, industry] of Object.entries(industryMap)) {
      if (!industry.dataTypes) continue;

      const industryData = [];

      // Process each item
      for (const item of filteredItems) {
        // Process crop data
        if (industry.dataTypes.crop && item.crop && industryKey === "farming") {
          const entry = mapData({ item }, industry.dataTypes.crop.template, item.id);
          if (entry) industryData.push(entry);
        }

        // Process produce data
        if (industry.dataTypes.produce && item.produce) {
          for (const produce of item.produce) {
            if (evalFilter({ produce, item }, industry.dataTypes.produce.filter)) {
              const entry = mapData({ item, produce }, industry.dataTypes.produce.template, item.id);
              if (entry) industryData.push(entry);
            }
          }
        }

        // Process recipes
        if (industry.dataTypes.recipes) {
          const recipes = [];

          // Collect recipes from both sources
          if (item.recipes?.length) {
            for (const recipe of item.recipes) {
              recipes.push({
                ...recipe,
                itemId: item.id,
                tier: item.tier,
              });
            }
          }

          for (const recipe of recipes) {
            if (!excludedItemsSet.has(recipe.achievementId) && !excludedItemsSet.has(recipe.itemId)) {
              try {
                if (evalFilter({ recipe }, industry.dataTypes.recipes.filter)) {
                  const entry = mapData({ recipe, item }, industry.dataTypes.recipes.template, item.id);
                  if (entry) industryData.push(entry);
                }
              } catch (e) {
                console.error("Error processing recipe:", recipe, e);
              }
            }
          }
        }
      }

      // Sort if we have data
      if (industryData.length > 0) {
        industryData.sort((a, b) => {
          const levelDiff = (a.Level || 0) - (b.Level || 0);
          return levelDiff !== 0 ? levelDiff : (a.Tier || 0) - (b.Tier || 0);
        });
        result[industryKey] = industryData;
      }
    }

    return result;
  }, [items, mapData]);

  useEffect(() => {
    if (items.length > 0) {
      const data = processItems();
      setProcessedData(data);
    }
  }, [items, processItems]);

  const evalFilter = (context, filterExpression) => {
    if (!filterExpression) return true; // No filter means include all

    try {
      // eslint-disable-next-line no-new-func
      const filterFunc = new Function(...Object.keys(context), `return ${filterExpression}`);

      // eslint-disable-next-line no-new-func
      const result = filterFunc(...Object.values(context));
      return result;
    } catch (e) {
      console.error("Filter Evaluation Error:", e);
      console.error("Failed Expression:", filterExpression);
      console.error("Context:", context);
      return false;
    }
  };

  const getValue = (obj, path) => {
    if (!path) return undefined;
    return path.split(".").reduce((acc, part) => {
      if (acc === null || acc === undefined) return undefined;
      return acc[part];
    }, obj);
  };

  const getTableHeaders = (industryKey) => {
    const industry = industryMap[industryKey];
    if (!industry) return [];

    if (industry.headings) return industry.headings.filter((h) => h !== "id");

    if (processedData[industryKey]?.length > 0) {
      return Object.keys(processedData[industryKey][0]).filter((key) => key !== "id");
    }

    return [];
  };

  const renderTableCell = (data, header) => {
    if (data === null || data === undefined) return "-";

    // Handle Industry column mapping
    if (header === "Industry") {
      return industryMapping[data] || data;
    }

    // Handle Item column (image + name)
    if (header === "Item" && typeof data === "object" && data.image && data.name) {
      return (
        <div className="inline-image-text cursor-pointer" onClick={() => handleOpenModal(data.id)}>
          <img src={data.image} alt={data.name} width={24} height={24} className="inline" />
          <span> {data.name}</span>
        </div>
      );
    }

    // Handle Feed column (id + name) - fetch the feed item separately
    if (header === "Feed" && typeof data === "object" && data.feedId && data.name) {
      return (
        <div className="cursor-pointer hover:underline" onClick={() => handleOpenModal(data.feedId)}>
          <span>{data.name}</span>
        </div>
      );
    }

    if (header === "Result" && typeof data === "object" && data.resultId && data.resultName) {
      return (
        <div className="cursor-pointer hover:underline" onClick={() => handleOpenModal(data.resultId)}>
          <span>{data.resultName}</span>
        </div>
      );
    }

    // Handle Recipe ingredients
    if (header === "Recipe" && typeof data === "object" && data.ingredients) {
      return (
        <div className="ingredients-list">
          {data.ingredients.map((ing, i) => (
            <div
              key={i}
              className="inline-image-text ingredient-item cursor-pointer"
              onClick={() => {
                handleOpenModal(ing.id);
              }}
            >
              <img src={ing.image} alt={ing.name} width={20} height={20} className="inline" />
              <span className="ingredient-quantity"> {ing.quantity} x </span>
              <span>{ing.name}</span>
            </div>
          ))}
        </div>
      );
    }

    // Handle Exp/Energy NaN
    if (header === "Exp/Energy" && (isNaN(data) || !isFinite(data))) return "N/A";

    // Default case
    return typeof data === "object" ? JSON.stringify(data) : data.toString();
  };

  if (isLoading && availableTabs.length === 0) {
    return <div className="loading">Loading initial data...</div>;
  }

  const handleSort = (key) => {
    setSortConfig((prev) => {
      const isSameKey = prev.key === key;
      return {
        key,
        direction: isSameKey && prev.direction === "ascending" ? "descending" : "ascending",
      };
    });
  };

  const getFilteredData = () => {
    const data = processedData[activeTab];
    if (!data) return [];

    // First filter the data
    const filtered = data.filter((row) => row.Item?.name?.toLowerCase().includes(searchQuery.toLowerCase()));

    // Then sort the filtered data
    const sorted = [...filtered].sort((a, b) => {
      const valA = a[sortConfig.key];
      const valB = b[sortConfig.key];

      if (valA == null) return 1;
      if (valB == null) return -1;

      if (typeof valA === "string") {
        return sortConfig.direction === "ascending" ? valA.localeCompare(valB) : valB.localeCompare(valA);
      }

      return sortConfig.direction === "ascending" ? valA - valB : valB - valA;
    });

    return sorted;
  };

 return (
    <div className="skills-breakdown-container">
      <div className="skills-tabs">
        {availableTabs.map((key) => {
          const industry = industryMap[key];
          const hasData = !!processedData[key];

          return (
            <button
              key={key}
              className={`skill-tab ${activeTab === key ? "active" : ""}`}
              onClick={() => handleTabChange(key)}
              aria-label={industry.name}
              disabled={isLoading && !hasData}
            >
              <img src={industry.image} alt={industry.name} className="skill-icon" />
              {isLoading && !hasData && <span className="tab-loading-indicator"></span>}
            </button>
          );
        })}
      </div>

      <div className="skill-content">
        {activeTab ? (
          isLoading && !processedData[activeTab] ? (
            <div className="flex items-center justify-center p-8">
              <div className="text-center">
                <div className="loading-spinner mb-3"></div>
                <div className="text-lg">Loading {industryMap[activeTab]?.name} data...</div>
              </div>
            </div>
          ) : processedData[activeTab] ? (
            <div className="data-table-container">
              <div className="table-scroll">
                <input
                  type="text"
                  placeholder="Search items..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value.toLowerCase())}
                  className="select-forms p-2 m-1"
                />

                <div className="max-h-[70vh] overflow-auto">
                  <table className="market-table">
                    <thead>
                      <tr>
                        {getTableHeaders(activeTab).map((header) => (
                          <th key={header} onClick={() => handleSort(header)} className="cursor-pointer">
                            {header}
                            {sortConfig.key === header && (sortConfig.direction === "ascending" ? " ▲" : " ▼")}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredData().map((row, index) => (
                        <tr key={index}>
                          {getTableHeaders(activeTab).map((header) => (
                            <td key={header}>{renderTableCell(row[header], header)}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-center p-8">
              <div className="text-center text-gray-500">
                No data available for {industryMap[activeTab]?.name}
              </div>
            </div>
          )
        ) : (
          <div className="flex items-center justify-center p-8">
            <div className="text-center text-gray-500">Select a skill to view data</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SkillsBreakdown;